import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, UnitNumber, Select, Result,
  SuffixNum,
  ampsToKw, kwToAmps, fmt,
  SpecCard, SpecValue,
} from "./_shared";

export default function AmpsKwConverter() {
  const [mode, setMode] = useState("amps-to-kw");
  const [phase, setPhase] = useState("3");
  const [volts, setVolts] = useState(400);
  const [pf, setPf] = useState(0.85);
  const [amps, setAmps] = useState(10);
  const [powerVal, setPowerVal] = useState(5);
  const [powerUnit, setPowerUnit] = useState("kW");      // kW | hp

  const threePhase = phase === "3";

  const onPhaseChange = (next) => {
    setPhase(next);
    if (next === "3" && volts === 230) setVolts(400);
    if (next === "1" && volts === 400) setVolts(230);
  };

  // Power input: convert hp → kW for internal calculation.
  const kw_in = powerUnit === "hp" ? powerVal * 0.7457 : powerVal;

  const kwOut   = useMemo(() => ampsToKw({ amps, volts, pf, threePhase }), [amps, volts, pf, threePhase]);
  const ampsOut = useMemo(() => kwToAmps({ kw: kw_in, volts, pf, threePhase }), [kw_in, volts, pf, threePhase]);
  const kwShown  = mode === "amps-to-kw" ? kwOut : kw_in;
  const kvaShown = kwShown !== null ? kwShown / pf : null;
  const hpShown  = kwShown !== null ? kwShown * 1.34102 : null;

  // Simple input row (no unit selector — units are fixed for electrical here).
  // Uses the shared SuffixNum which has the proper local-buffer state so the
  // user can type '0', '0.', '0.5', '.5', '0,5' etc without their keystrokes
  // being wiped by the numeric round-trip.
  const numberInput = (val, setter, suffix, testid) => (
    <SuffixNum value={val} onChange={setter} suffix={suffix} testid={testid} />
  );

  return (
    <ToolShell
      slug="amps-kw"
      title="Amps ↔ kW converter"
      oneLiner="Convert between current draw and motor power for single- and three-phase circuits. Power factor defaults to 0.85 (typical for VSD-driven pumps)."
      onExport={() => ({
        inputs: [
          { label: "Direction", value: mode === "amps-to-kw" ? "Amps → kW" : "kW → Amps" },
          { label: "Supply phase", value: threePhase ? "Three phase" : "Single phase" },
          { label: "Voltage", value: volts, unit: "V" },
          { label: "Power factor", value: pf },
          mode === "amps-to-kw"
            ? { label: "Current draw", value: amps, unit: "A" }
            : { label: "Motor power", value: powerVal, unit: powerUnit },
        ],
        results: [
          {
            label: mode === "amps-to-kw" ? "Active power" : "Current draw",
            value: mode === "amps-to-kw" ? fmt(kwOut) : fmt(ampsOut),
            unit: mode === "amps-to-kw" ? "kW" : "A",
            alts: mode === "amps-to-kw" && kwOut !== null
              ? [{ value: fmt(hpShown), unit: "hp" }] : null,
          },
          { label: "Apparent power", value: fmt(kvaShown), unit: "kVA" },
          { label: "Annual energy (continuous)",
            value: fmt(kwShown ? kwShown * 24 * 365 : null, 0), unit: "kWh / year",
            note: "Continuous duty · scale by your actual duty cycle." },
        ],
        notes: [
          "Single phase:  kW = V × A × PF / 1000.",
          "Three phase :  kW = √3 × V × A × PF / 1000.",
        ],
        spec: kwShown !== null ? {
          title: "Power conversion",
          body: mode === "amps-to-kw"
            ? `On a <strong>${threePhase ? "three-phase 400 V" : "single-phase 230 V"}</strong> supply at PF <strong>${pf}</strong>, a current of <strong>${amps} A</strong> equals <strong>${fmt(kwOut, 2)} kW</strong> active (<strong>${fmt(kvaShown, 2)} kVA</strong> apparent, <strong>${fmt(hpShown, 2)} hp</strong>). Continuous duty ≈ <strong>${fmt(kwShown * 24 * 365, 0)} kWh/year</strong>.`
            : `On a <strong>${threePhase ? "three-phase 400 V" : "single-phase 230 V"}</strong> supply at PF <strong>${pf}</strong>, <strong>${powerVal} ${powerUnit}</strong> draws <strong>${fmt(ampsOut, 2)} A</strong> (<strong>${fmt(kvaShown, 2)} kVA</strong> apparent). Continuous duty ≈ <strong>${fmt(kwShown * 24 * 365, 0)} kWh/year</strong>.`,
        } : null,
      })}
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Conversion direction">
          <Select value={mode} onChange={setMode} options={[
            { value: "amps-to-kw", label: "Amps → kW" },
            { value: "kw-to-amps", label: "kW → Amps" },
          ]} testid="ak-mode" />
        </Field>
        <Field label="Supply phase">
          <Select value={phase} onChange={onPhaseChange} options={[
            { value: "1", label: "Single phase (230 V default)" },
            { value: "3", label: "Three phase (400 V default)" },
          ]} testid="ak-phase" />
        </Field>
        <Field label="Voltage">
          {numberInput(volts, setVolts, "V", "ak-volts")}
        </Field>
        <Field label="Power factor" hint="0.85 typical for VSD motors · 1.00 for resistive loads.">
          {numberInput(pf, setPf, "PF", "ak-pf")}
        </Field>
        {mode === "amps-to-kw"
          ? <Field label="Current draw" span={2}>{numberInput(amps, setAmps, "A", "ak-amps")}</Field>
          : <Field label="Motor power"  span={2}>
              <div className="flex items-stretch">
                <div className="flex-1 min-w-0">
                  <SuffixNum value={powerVal} onChange={setPowerVal} testid="ak-kw" />
                </div>
                <select value={powerUnit} onChange={(e) => setPowerUnit(e.target.value)}
                  className="px-2 border border-l-0 bg-[#FAFAF7] outline-none focus:border-[#1FB6C1]"
                  style={{ borderColor: "rgba(12,42,64,0.16)", color: "rgba(12,42,64,0.78)", fontSize: "0.75rem", minWidth: 78 }}
                  data-testid="ak-kw-unit">
                  <option value="kW">kW</option>
                  <option value="hp">hp</option>
                </select>
              </div>
            </Field>}
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <Result
          label={mode === "amps-to-kw" ? "Active power" : "Current draw"}
          primary={{
            value: mode === "amps-to-kw" ? fmt(kwOut) : fmt(ampsOut),
            unit: mode === "amps-to-kw" ? "kW" : "A",
          }}
          alts={mode === "amps-to-kw" && kwOut !== null
            ? [{ value: fmt(hpShown), unit: "hp" }]
            : null}
          testid="ak-result-main"
        />
        <Result
          label="Apparent power"
          primary={{ value: fmt(kvaShown), unit: "kVA" }}
          testid="ak-result-kva"
        />
        <Result
          label="Annual energy (continuous)"
          primary={{ value: fmt(kwShown ? kwShown * 24 * 365 : null, 0), unit: "kWh / year" }}
          note="Continuous duty · scale by your actual duty cycle."
          testid="ak-result-energy"
        />
      </div>

      {kwShown !== null && (
        <SpecCard
          eyebrow="Power conversion"
          testid="ak-spec-card"
        >
          {mode === "amps-to-kw" ? (
            <>
              On a <SpecValue>{threePhase ? "three-phase 400 V" : "single-phase 230 V"}</SpecValue> supply at PF{" "}
              <SpecValue>{pf}</SpecValue>, a current of <SpecValue>{amps} A</SpecValue> equals{" "}
              <SpecValue>{fmt(kwOut, 2)} kW</SpecValue> active (<SpecValue>{fmt(kvaShown, 2)} kVA</SpecValue> apparent,{" "}
              <SpecValue>{fmt(hpShown, 2)} hp</SpecValue>). Continuous duty ≈{" "}
              <SpecValue>{fmt(kwShown * 24 * 365, 0)} kWh/year</SpecValue>.
            </>
          ) : (
            <>
              On a <SpecValue>{threePhase ? "three-phase 400 V" : "single-phase 230 V"}</SpecValue> supply at PF{" "}
              <SpecValue>{pf}</SpecValue>, <SpecValue>{powerVal} {powerUnit}</SpecValue> draws{" "}
              <SpecValue>{fmt(ampsOut, 2)} A</SpecValue> (<SpecValue>{fmt(kvaShown, 2)} kVA</SpecValue> apparent).
              Continuous duty ≈ <SpecValue>{fmt(kwShown * 24 * 365, 0)} kWh/year</SpecValue>.
            </>
          )}
        </SpecCard>
      )}
    </ToolShell>
  );
}
